import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = (process.env.REACT_APP_SUPABASE_URL || "").trim();
const SUPABASE_ANON_KEY = (
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  ""
).trim();

let supabaseClient = null;
const SUPABASE_CODE_VERIFIER_SUFFIX = "-code-verifier";
const BLOCKED_AUTH_STORAGE_VALUE_MARKERS = [
  "access_token",
  "refresh_token",
  "provider_token",
  "provider_refresh_token",
];

function getWindowObject() {
  return typeof window !== "undefined" ? window : null;
}

function getSupabaseStorageKey() {
  if (!SUPABASE_URL) return "";

  try {
    const { hostname } = new URL(SUPABASE_URL);
    const projectRef = hostname.split(".")[0];
    return projectRef ? `sb-${projectRef}-auth-token` : "";
  } catch {
    return "";
  }
}

function getSupabasePkceVerifierStorageKey() {
  const storageKey = getSupabaseStorageKey();
  return storageKey ? `${storageKey}${SUPABASE_CODE_VERIFIER_SUFFIX}` : "";
}

function valueContainsAuthSessionToken(value) {
  if (typeof value !== "string") return false;
  const normalized = value.toLowerCase();
  return BLOCKED_AUTH_STORAGE_VALUE_MARKERS.some((marker) =>
    normalized.includes(marker)
  );
}

function removeBrowserStorageItem(win, key) {
  if (!win || !key) return;

  try {
    win.localStorage.removeItem(key);
  } catch {
    // Best-effort browser storage cleanup.
  }

  try {
    win.sessionStorage.removeItem(key);
  } catch {
    // Best-effort browser storage cleanup.
  }
}

function createPkceOnlyStorageAdapter(win = getWindowObject()) {
  const pkceVerifierKey = getSupabasePkceVerifierStorageKey();

  return {
    getItem(key) {
      if (!win || key !== pkceVerifierKey) {
        removeBrowserStorageItem(win, key);
        return null;
      }

      const value = win.sessionStorage.getItem(key);
      if (valueContainsAuthSessionToken(value)) {
        removeBrowserStorageItem(win, key);
        return null;
      }

      return value;
    },

    setItem(key, value) {
      if (!win || key !== pkceVerifierKey || valueContainsAuthSessionToken(value)) {
        removeBrowserStorageItem(win, key);
        return;
      }

      win.sessionStorage.setItem(key, value);
      try {
        win.localStorage.removeItem(key);
      } catch {
        // Best-effort browser storage cleanup.
      }
    },

    removeItem(key) {
      removeBrowserStorageItem(win, key);
    },
  };
}

function removeKnownSupabaseStorageKeys(win = getWindowObject()) {
  if (!win) return;

  const storageKey = getSupabaseStorageKey();
  if (!storageKey) return;

  const keys = [
    storageKey,
    `${storageKey}-code-verifier`,
    `${storageKey}-user`,
  ];

  for (const key of keys) {
    try {
      win.localStorage.removeItem(key);
      win.sessionStorage.removeItem(key);
    } catch {
      // Best-effort browser storage cleanup.
    }
  }
}

export function isSupabaseOAuthConfigured() {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
}

export function getSupabaseClient() {
  if (!isSupabaseOAuthConfigured()) {
    throw new Error("supabase_oauth_not_configured");
  }

  if (!supabaseClient) {
    supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        autoRefreshToken: false,
        detectSessionInUrl: false,
        flowType: "pkce",
        persistSession: true,
        storage: createPkceOnlyStorageAdapter(),
        storageKey: getSupabaseStorageKey(),
      },
    });
  }

  return supabaseClient;
}

export async function clearSupabaseBrowserSession({ logWarning = false } = {}) {
  removeKnownSupabaseStorageKeys();

  if (!isSupabaseOAuthConfigured()) {
    return;
  }

  try {
    const supabase = getSupabaseClient();
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    if (logWarning) {
      console.warn("Supabase browser session cleanup did not complete.");
    }
  } finally {
    removeKnownSupabaseStorageKeys();
  }
}
